/* Bajar el histórico de urea a Excel.
 *
 * Nico, 16/09: *«Necesito poder exportar a excel los datos de urea»*.
 *
 * ── SALE LO QUE ESTÁ EN LA PANTALLA ──────────────────────────────────────────
 *
 * Recibe las filas YA filtradas y YA ordenadas por `routes/Urea`, no las vuelve
 * a pedir ni a ordenar. Es la misma decisión que `exportarPresidencia`, y por
 * el mismo motivo: el que baja el archivo lo va a comparar contra la pantalla
 * desde la que lo bajó, y si no coinciden se entera delante de otro.
 *
 * ── YA HABÍA UN EXCEL DE UREA, Y NO ES ÉSTE ──────────────────────────────────
 *
 * El bot sirve `/urea?xlsx=1&desde=&hasta=`, que baja el PARTE: el mismo
 * resumen que sale por mail, con los huecos del talonario. Éste baja el
 * HISTÓRICO fila por fila, que es lo que se mira en el hub. Son dos preguntas
 * distintas —«¿qué vales faltan?» contra «¿qué se cargó?»— y por eso conviven
 * en vez de reemplazarse.
 *
 * ── LA LIBRERÍA YA ESTABA ────────────────────────────────────────────────────
 *
 * `xlsx` es dependencia del proyecto desde antes (la usan Presidencia y la
 * importación de vacaciones). No se agrega ninguna.
 */

import { rotuloDe } from "../shared/talonario";

import { sinHora } from "../shared/fechaAr";

const TZ = "America/Argentina/Buenos_Aires";

/* `2026-09-15 13:42`, en hora de ACÁ.
 *
 * No va como fecha de Excel a propósito. Una `Date` de JavaScript se convierte
 * al serial de Excel contra UTC, así que la carga de las 22:00 de un lunes
 * bajaría fechada el martes a la 01:00 — tres horas de corrimiento que nadie
 * mira hasta que discute un consumo con el número equivocado en la mano.
 *
 * El formato es AAAA-MM-DD a propósito: ordena bien como texto, no se confunde
 * con DD/MM ni con MM/DD, y Excel igual lo reconoce como fecha si alguien
 * quiere agrupar por mes.
 */
export function fechaHora(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const p = new Intl.DateTimeFormat("sv-SE", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(d);
  // Sin hora real (vale de otro día, guardado a las 12:00:00 en punto) va
  // sólo el día: «12:00» sería una hora inventada. Ver `sinHora`.
  if (sinHora(iso)) return p.slice(0, 10);
  return p.replace("T", " ");
}

export const CABECERA = ["Cuándo", "Interno", "Litros", "Vale", "Odómetro",
                         "Producto", "Talonario", "Cargó", "Foto del vale"];

/* Un número o vacío. NUNCA un cero inventado: en una planilla de consumo, un
 * cero es un dato —«cargó y no salió nada»— y un vacío es «no lo sabemos».
 * Escribir 0 donde falta el odómetro hace un promedio que nadie puede
 * explicar. */
const num = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? n : "";
};

/* La matriz que consume Excel. Separada del armado del archivo para poder
 * fijarla en un test sin escribir un .xlsx. */
export function comoMatriz(filas) {
  return [
    CABECERA,
    ...(filas || []).map((c) => [
      fechaHora(c.fecha),
      c.interno || "",
      num(c.litros),
      c.vale || "",
      num(c.km),
      c.producto || "",
      rotuloDe(c),
      c.quien_nombre || c.quien || "",
      c.foto ? "sí" : "no",
    ]),
  ];
}

/* El nombre lleva DE CUÁNDO A CUÁNDO. Se bajan varios y quedan en Descargas:
 * `urea (3).xlsx` no dice de qué período es ninguno, y ahí se abren los tres
 * para encontrar el que servía. */
export function nombreArchivo(desde, hasta) {
  if (!desde && !hasta) return "urea";
  if (desde === hasta) return `urea-${desde}`;
  return `urea-${desde || "inicio"}_a_${hasta || "hoy"}`;
}

export async function aExcel(filas, desde, hasta) {
  const XLSX = await import("xlsx");
  const datos = comoMatriz(filas);
  const hoja = XLSX.utils.aoa_to_sheet(datos);
  hoja["!cols"] = [{ wch: 17 }, { wch: 9 }, { wch: 9 }, { wch: 11 },
                   { wch: 11 }, { wch: 11 }, { wch: 22 }, { wch: 24 },
                   { wch: 13 }];
  // La fila de títulos queda fija: con quinientas cargas, a la décima pantalla
  // ya no se sabe qué columna es cuál. Es lo mismo que hace el Excel del parte
  // del lado del bot.
  hoja["!freeze"] = { xSplit: 0, ySplit: 1 };
  const libro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(libro, hoja, "Cargas de urea");
  XLSX.writeFile(libro, `${nombreArchivo(desde, hasta)}.xlsx`);
}
